import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  IconButton
} from '@mui/material'
import DiscountCodeForm from 'components/DiscountCodeForm'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

interface CartSummaryProps {
  subTotal: string
  discountedAmount: string
  tax: string
  assemblyFee: string
  totalPrice: string
  discountResult: { isValid?: boolean; rate?: number } | null
  validateDiscountCode: (code: string) => void
  removeDiscount: () => void
  discountCode: string
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>
  handleCreateQuote: () => void
  handleCheckout: () => void
  poNumber: string
  onUpdatePoNumber: (po: string) => void
  isCheckoutDisabled?: boolean
}

const CartSummary = ({
  subTotal,
  discountedAmount,
  tax,
  assemblyFee,
  totalPrice,
  discountResult,
  validateDiscountCode,
  removeDiscount,
  discountCode,
  setDiscountCode,
  handleCreateQuote,
  handleCheckout,
  poNumber,
  onUpdatePoNumber,
  isCheckoutDisabled = false
}: CartSummaryProps) => {
  const { t } = useTranslation()
  const [isDiscountFormVisible, setIsDiscountFormVisible] = useState(false)
  const [editingPoNumber, setEditingPoNumber] = useState(false)

  const { register, handleSubmit, reset } = useForm<{
    poNumber: string
  }>({
    defaultValues: { poNumber: poNumber || '' }
  })

  const handleStartEditPoNumber = () => {
    setEditingPoNumber(true)
    reset({ poNumber: poNumber || '' })
  }

  const handleCancelEditPoNumber = () => {
    setEditingPoNumber(false)
    reset({ poNumber: poNumber || '' })
  }

  const onSubmitPoNumber = (data: { poNumber: string }) => {
    onUpdatePoNumber(data.poNumber)
    setEditingPoNumber(false)
  }

  return (
    <Box
      sx={{
        bgcolor: '#F8F8F8',
        borderRadius: 2,
        width: '350px',
        display: 'flex',
        flexDirection: 'column',
        padding: '30px',
        height: 'fit-content'
      }}
    >
      <Typography variant='h3'>{t('cart.summary.title')}</Typography>

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>{t('cart.summary.subtotal')}</Typography>
        <Typography variant='body1'>${subTotal}</Typography>
      </Box>

      {discountResult?.isValid && (
        <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
          <Typography variant='body1'>
            {t('cart.summary.discount')} ({discountResult.rate}%)
          </Typography>
          <Typography variant='body1' sx={{ color: 'green' }}>
            -${discountedAmount}
          </Typography>
        </Box>
      )}

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>{t('cart.summary.tax')}</Typography>
        <Typography variant='body1'>${tax}</Typography>
      </Box>

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>{t('cart.summary.assemblyFee')}</Typography>
        <Typography variant='body1'>${assemblyFee}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='h2'>{t('cart.summary.total')}</Typography>
        <Typography variant='h2'>${totalPrice}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display='flex' alignItems='center' sx={{ my: 1 }}>
        {editingPoNumber ? (
          <form onSubmit={handleSubmit(onSubmitPoNumber)}>
            <Box display='flex' alignItems='center' gap={1}>
              <TextField
                size='small'
                label={t('cart.summary.poNumber')}
                {...register('poNumber')}
                inputProps={{ maxLength: 20 }}
              />
              <IconButton color='primary' type='submit'>
                <CheckIcon />
              </IconButton>
              <IconButton color='inherit' onClick={handleCancelEditPoNumber}>
                <CloseIcon />
              </IconButton>
            </Box>
          </form>
        ) : (
          <>
            <Typography variant='body1'>
              {t('cart.summary.poNumber')}: {poNumber || '___'}
            </Typography>
            <IconButton size='small' onClick={handleStartEditPoNumber}>
              <EditIcon />
            </IconButton>
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <DiscountCodeForm
        discountCode={discountCode}
        setDiscountCode={setDiscountCode}
        isDiscountFormVisible={isDiscountFormVisible}
        setIsDiscountFormVisible={setIsDiscountFormVisible}
        validateDiscountCode={validateDiscountCode}
        discountResult={discountResult}
        onRemoveDiscount={() => {
          removeDiscount()
          setIsDiscountFormVisible(false)
        }}
      />

      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          my: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Button
          variant='outlined'
          sx={{
            mb: 2,
            borderColor: 'primary.main',
            padding: '10px 40px',
            borderRadius: '4px',
            borderWidth: '1px',
            width: '290px',
            height: '46px'
          }}
          onClick={handleCreateQuote}
        >
          {t('cart.summary.saveAsQuote')}
        </Button>
        <Button
          variant='contained'
          sx={{
            bgcolor: 'primary.main',
            padding: '10px 40px',
            borderRadius: '4px',
            borderWidth: '1px',
            width: '290px',
            height: '46px'
          }}
          onClick={handleCheckout}
          disabled={isCheckoutDisabled}
        >
          {t('cart.summary.proceedToCheckout')}
        </Button>
      </Box>
    </Box>
  )
}

export default CartSummary
